import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { ArrowLeftThin } from '@ori-ui/icons';
import { ButtonBase, FormControl, FormControlLabel, Radio, RadioGroup, TextField, Typography, styled } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import { useFormik } from 'formik';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { TEMPORARY_PASSWORD_URL } from '../../constants';
import { useConfigurationContext } from '../../contexts/ConfigurationContext';
import { useSocialMediaContext } from '../../contexts/SocialMediaContext';
import { getCustomAxios, handleAxiosError } from '../../helpers';
import { AccountPanes } from '../../models';
import { LocalizationKeys } from '../../models/localizationKeys';
import { InputErrorIcon } from './InputErrorIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var ForgotPasswordRoot = styled('div')(function (_ref) {
  var theme = _ref.theme;
  return {
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    height: 'calc(100% - var(--top-nav-height))',
    padding: theme.spacing(3, 4)
  };
});
var ForgotPasswordHeader = styled('div')(function (_ref2) {
  var theme = _ref2.theme;
  return {
    display: 'flex',
    marginBottom: theme.spacing(6)
  };
});
var ForgotPasswordGoBackButton = styled(ButtonBase)(function (_ref3) {
  var theme = _ref3.theme;
  return {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(28),
    '&:hover, &:focus, &:active': {
      color: theme.palette.text.disabled
    }
  };
});
var ForgotPasswordForm = styled('form')(function () {
  return {
    display: 'flex',
    flexDirection: 'column'
  };
});
var ForgotPasswordHeaderTitle = styled(Typography)(function (_ref4) {
  var theme = _ref4.theme;
  return {
    color: theme.palette.text.secondary,
    lineHeight: theme.typography.pxToRem(27),
    marginRight: theme.spacing(5),
    textAlign: 'center',
    width: '100%'
  };
});
var ForgotPasswordDescription = styled('div')(function (_ref5) {
  var theme = _ref5.theme;
  return {
    color: theme.palette.text.primary,
    fontSize: theme.typography.body1.fontSize,
    lineHeight: theme.typography.pxToRem(19),
    marginBottom: theme.spacing(4)
  };
});
var ForgotPasswordError = styled('div')(function (_ref6) {
  var theme = _ref6.theme;
  return {
    color: theme.palette.error.main,
    margin: theme.spacing(0, 2)
  };
});
var ForgotPasswordUsernameInput = styled(TextField)(function (_ref7) {
  var theme = _ref7.theme;
  return {
    height: 38,
    width: '100%'
  };
});
var ForgotPasswordContinueButton = styled(ButtonBase)(function (_ref8) {
  var theme = _ref8.theme;
  return {
    background: theme.palette.primary.main,
    borderRadius: 20,
    color: theme.palette.common.white,
    fontSize: theme.typography.body1.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: theme.typography.pxToRem(22),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2, 0),
    textTransform: 'uppercase'
  };
});
var ForgotPasswordNewPasswordButton = styled(ButtonBase)(function (_ref9) {
  var theme = _ref9.theme;
  return {
    background: 'none',
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body1.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: theme.typography.pxToRem(17),
    textDecoration: 'underline',
    '&:hover, &:focus, &:active': {
      color: theme.palette.text.disabled
    }
  };
});
var ForgotPasswordUsernameInfo = styled('div')(function () {
  return {
    display: 'flex',
    position: 'absolute',
    right: 0,
    top: '50%'
  };
});
var ForgotPasswordUsernameRoot = styled('div')(function (_ref10) {
  var theme = _ref10.theme;
  return {
    marginBottom: theme.spacing(4),
    position: 'relative'
  };
});
export var ForgotPassword = function ForgotPassword(_ref11) {
  var goToPane = _ref11.goToPane;

  var _useTranslation = useTranslation(),
      _useTranslation2 = _slicedToArray(_useTranslation, 1),
      translate = _useTranslation2[0];

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      errorMessage = _useState2[0],
      setErrorMessage = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isSubmitLocked = _useState4[0],
      setIsSubmitLocked = _useState4[1];

  var _useSocialMediaContex = useSocialMediaContext(),
      forgottenPassChoices = _useSocialMediaContex.forgottenPassChoices;

  var _useConfigurationCont = useConfigurationContext(),
      currentLanguage = _useConfigurationCont.currentLanguage,
      onlineBaseUrl = _useConfigurationCont.onlineBaseUrl;

  var sendTempPassUrl = new URL(TEMPORARY_PASSWORD_URL, onlineBaseUrl !== null && onlineBaseUrl !== void 0 ? onlineBaseUrl : window.location.origin);
  var usernameLabel = "".concat(translate(LocalizationKeys.emailAddressOrRegistrationNumber.id), "*");
  var labels = {
    0: 'Email',
    1: 'SMS'
  };
  var handleGoBackLinkClicked = useCallback(function () {
    return goToPane(AccountPanes.LoginPane);
  }, [goToPane]);
  var handleNewPasswordLinkClicked = useCallback(function () {
    return goToPane(AccountPanes.NewPassPane);
  }, [goToPane]);
  var sendTempPassSchema = Yup.object().shape({
    SelectedChannel: forgottenPassChoices && forgottenPassChoices.length > 0 ? Yup.string().trim().required() : Yup.string(),
    Username: Yup.string().required(translate(LocalizationKeys.enterEmailOrBrandPartnerNumber.id))
  });

  var _useTestId = useTestId(),
      getTestId = _useTestId.getTestId;

  var isSelectedChannel = function isSelectedChannel(channelValues) {
    if (channelValues.SelectedChannel) {
      return {
        Username: channelValues.Username,
        SelectedChannel: Number(channelValues.SelectedChannel)
      };
    }

    return {
      Username: channelValues.Username
    };
  };

  var onSendTempPassSubmit = function onSendTempPassSubmit() {
    return async function (formValues, actions) {
      actions.setSubmitting(false);
      setErrorMessage(null);

      if (!isSubmitLocked) {
        setIsSubmitLocked(true);
        var sendPassPayload = isSelectedChannel(formValues);

        try {
          var sendTempPassResponse = await getCustomAxios(currentLanguage).post(sendTempPassUrl.toString(), sendPassPayload);

          if (sendTempPassResponse.data.Success) {
            goToPane(AccountPanes.NewPassPane, formValues.Username);
          }

          if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console -- Will be removed after error handling will be known, local environment is not working correctly for login.
            console.log(sendTempPassResponse.data);
          }
        } catch (error) {
          handleAxiosError({
            callback: function callback() {
              return setIsSubmitLocked(false);
            },
            error: error,
            translateMessage: function translateMessage(key) {
              return setErrorMessage(translate(key));
            }
          });
        }
      }
    };
  };

  var _useFormik = useFormik({
    initialValues: {
      Username: '',
      SelectedChannel: ''
    },
    validationSchema: sendTempPassSchema,
    onSubmit: onSendTempPassSubmit()
  }),
      errors = _useFormik.errors,
      touched = _useFormik.touched,
      values = _useFormik.values,
      handleSubmit = _useFormik.handleSubmit,
      handleChange = _useFormik.handleChange,
      handleBlur = _useFormik.handleBlur;

  return /*#__PURE__*/_jsxs(ForgotPasswordRoot, {
    children: [/*#__PURE__*/_jsxs(ForgotPasswordHeader, {
      children: [/*#__PURE__*/_jsx(ForgotPasswordGoBackButton, {
        disableRipple: true,
        onClick: handleGoBackLinkClicked,
        children: /*#__PURE__*/_jsx(ArrowLeftThin, {
          fontSize: "inherit"
        })
      }), /*#__PURE__*/_jsx(ForgotPasswordHeaderTitle, {
        variant: "h6",
        children: translate(LocalizationKeys.forgottenPassword.id)
      })]
    }), /*#__PURE__*/_jsx(ForgotPasswordDescription, {
      children: translate(LocalizationKeys.forgotYourPasswordQuestion.id)
    }), /*#__PURE__*/_jsxs(ForgotPasswordForm, {
      onSubmit: handleSubmit,
      onBlur: handleBlur,
      children: [errorMessage && /*#__PURE__*/_jsx(ForgotPasswordError, {
        "data-testid": getTestId('forgot-password-error'),
        children: errorMessage
      }), forgottenPassChoices && forgottenPassChoices.length > 0 && /*#__PURE__*/_jsx(FormControl, {
        children: /*#__PURE__*/_jsx(RadioGroup, {
          row: true,
          name: "SelectedChannel",
          value: values.SelectedChannel,
          onChange: handleChange,
          children: forgottenPassChoices.map(function (choice) {
            return /*#__PURE__*/_jsx(FormControlLabel, {
              value: String(choice),
              control: /*#__PURE__*/_jsx(Radio, {}),
              label: labels[choice]
            }, choice);
          })
        })
      }), /*#__PURE__*/_jsxs(ForgotPasswordUsernameRoot, {
        children: [/*#__PURE__*/_jsx(ForgotPasswordUsernameInput, {
          fullWidth: true,
          error: touched.Username && Boolean(errors.Username),
          id: "Username",
          inputProps: {
            maxLength: 40
          },
          variant: "standard",
          label: usernameLabel,
          onChange: handleChange
        }), /*#__PURE__*/_jsx(ForgotPasswordUsernameInfo, {
          "data-testid": getTestId('forgot-password-info'),
          children: touched.Username && Boolean(errors.Username) && /*#__PURE__*/_jsx(InputErrorIcon, {
            error: String(errors.Username)
          })
        })]
      }), /*#__PURE__*/_jsx(ForgotPasswordContinueButton, {
        disableRipple: true,
        type: "submit",
        children: translate(LocalizationKeys.continue.id)
      })]
    }), /*#__PURE__*/_jsx(ForgotPasswordNewPasswordButton, {
      disableRipple: true,
      onClick: handleNewPasswordLinkClicked,
      children: translate(LocalizationKeys.iAlreadyHaveATempPassword.id)
    })]
  });
};
export default ForgotPassword;